import React, {useCallback, useEffect, useImperativeHandle, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent} from 'helpers/hooks/utils';
import utils from 'helpers/utils';
import FormHelperText from 'components/atoms/Helpers/FormHelperText/FormHelperText';
import InputLabel from 'components/atoms/Labels/InputLabel/InputLabel';
import InputContainer from 'components/atoms/Layout/InputContainer/InputContainer';
import dom from 'helpers/dom';
import StyledComponentField from 'components/molecules/Fields/ComponentField/ComponentField.styles';
import {default as ComponentBase} from 'components/organisms/Utils/Component/Component';
import {withMemo} from 'helpers/wrapper';

const ComponentField = withMemo(React.forwardRef((props, ref) => {
  const {
    id,
    name,
    label,
    value,
    size,
    inset,
    required,
    error,
    success,
    autoFocus,
    helperText,
    hideError,
    hideSuccess,
    onChange,
    onBlur,
    valueProp,
    Component,
    ComponentProps,
    FormHelperTextProps,
    InputLabelProps,
    ...innerProps
  } = useComponentProps({
    hideError: true,
    hideSuccess: true,
    ...props
  }, 'ComponentField', {
    static: ['inset', 'disabled', 'focused', 'error'],
    children: ['component', 'label', 'helper']
  });

  const innerRef = useRef(null);
  const componentRef = useRef(null);
  const valueRef = useRef({
    errors: false,
    ...value
  });

  const componentField = useMemo(() => ({
    refs: {
      ref: innerRef,
      componentRef
    },
    submit: (touch = true) => {
      return componentRef.current?.submit?.(touch);
    },
    validate: (touch = true) => {
      return componentRef.current?.validate?.(touch);
    },
    reset: (state) => {
      return componentRef.current?.reset?.(state);
    }
  }), []);

  useImperativeHandle(ref, () => componentField);

  useEffect(() => {
    if (autoFocus) {
      const focus = () => {
        return dom.focusElement(innerRef.current?.querySelector?.('.ComponentField-container'));
      }

      return utils.observeRetry(focus, 5);
    }
  }, [autoFocus]);

  const onChangeEvent = useEffectEvent(onChange);
  const onBlurEvent = useEffectEvent(onBlur);

  const handleChange = useCallback((value) => {
    onChangeEvent?.({
      target: {
        name: name,
        value: {
          value,
          errors: false
        }
      }
    });
  }, [onChangeEvent, name]);

  const handleBlur = useCallback((e) => {
    if (!e || !dom.isPartOfParent(e.relatedTarget, innerRef.current)) {
      onBlurEvent?.({
        target: {
          name: name,
          value: valueRef.current
        }
      });
    }
  }, [onBlurEvent, name]);

  const handleError = useCallback((errors) => {
    if (valueRef.current.errors !== errors) {
      valueRef.current.errors = errors;

      onChangeEvent?.({
        target: {
          name: name,
          value: {
            value: valueRef.current.value,
            errors
          }
        }
      });
    }
  }, [onChangeEvent, name]);

  const valueMemo = useMemo(() => {
    valueRef.current.errors = value.errors;
    if (!utils.compare(valueRef.current.value, value.value)) {
      valueRef.current.value = value.value;
    }
    return valueRef.current.value;
  }, [value]);

  innerProps.className = utils.flattenClassName(innerProps.className);

  return <StyledComponentField ref={innerRef}
                               error={hideError ? false : error}
                               success={hideSuccess ? false : success}
                               {...innerProps}>
    {!innerProps.hiddenLabel ?
      <InputLabel {...utils.cleanObject({
                    htmlFor: (!(innerProps.readOnly || innerProps.disabled) ? id : null),
                    component: (innerProps.readOnly || innerProps.disabled) ? 'span' : null,
                    shrink: innerProps.readOnly || innerProps.disabled || null
                  })}
                  {...InputLabelProps}
                  className={utils.classNames('ComponentField-label', InputLabelProps?.className)}>
        {label}
      </InputLabel> : null}
    <InputContainer className="ComponentField-container">
      <ComponentBase ref={componentRef}
                     Original={Component}
                     className="ComponentField-component"
                     onChange={handleChange}
                     onBlur={handleBlur}
                     onError={handleError}
                     required={required}
                     error={error}
                     success={success}
                     readOnly={innerProps.readOnly}
                     disabled={innerProps.disabled}
                     {...{
                       [valueProp]: valueMemo
                     }}
                     {...ComponentProps} />
      {!innerProps.hiddenHelperText ?
        <FormHelperText component="div" {...FormHelperTextProps}
                        className={utils.classNames('ComponentField-helper', FormHelperTextProps?.className)}>
          {((error && hideError) || (success && hideSuccess)) ? null : helperText}
        </FormHelperText> : null}
    </InputContainer>
  </StyledComponentField>
}));

ComponentField.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  id: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.any,
  value: PropTypes.any,
  inset: PropTypes.bool,
  size: PropTypes.string,
  helperText: PropTypes.any,
  hideError: PropTypes.any,
  hideSuccess: PropTypes.any,
  autoFocus: PropTypes.bool,
  valueProp: PropTypes.string,
  Component: PropTypes.any,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  ComponentProps: PropTypes.object,
  InputLabelProps: PropTypes.object,
  FormHelperTextProps: PropTypes.object
};

export default ComponentField;
